/**
 * Phase 8 base AC19 — Visit schedule banners.
 *
 * Two small banners for VisitDetailPage:
 *   - `OutOfWindowBanner`  — red banner on the current visit when it
 *     was flagged as a candidate protocol deviation (visit.flagged_deviation).
 *   - `NextVisitBanner`    — subject-scoped info bar showing the next
 *     scheduled visit + its target window + reminder-active pill.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertOctagon, BellRing, CalendarClock, Loader2 } from 'lucide-react';
import { visitSchedule, type VisitRow } from '@/api/client';
import { fmtDate, visitWhen } from '@/lib/dates';

// ---------------------------------------------------------------------------

export function OutOfWindowBanner({ visit }: { visit: VisitRow }) {
  if (visit.flagged_deviation !== 'true') return null;
  const days = visit.flagged_deviation_days ?? 0;
  const direction = days < 0 ? 'early' : 'late';
  const abs = Math.abs(days);
  return (
    <div
      className="rounded border border-danger-300 bg-danger-50 p-3 text-xs text-danger-800"
      role="alert"
    >
      <div className="flex items-center gap-2 font-semibold">
        <AlertOctagon className="h-4 w-4" />
        Candidate protocol deviation
      </div>
      <p className="mt-1 text-[11px]">
        This visit occurred {abs} day{abs === 1 ? '' : 's'} {direction} of its
        configured tolerance window. Flagged by the schedule sweep for review.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function NextVisitBanner({ subjectId }: { subjectId: string }) {
  const q = useQuery({
    queryKey: ['subject', 'next-visit', subjectId],
    queryFn: () => visitSchedule.nextForSubject(subjectId),
    enabled: !!subjectId,
    staleTime: 60_000,
  });

  if (q.isLoading) {
    return (
      <div className="rounded border border-surface-border bg-surface-muted/20 p-2 text-[11px] text-ink-muted">
        <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading next visit…
      </div>
    );
  }
  if (q.isError || !q.data) return null;

  const nv = q.data.next_visit;
  if (!nv) {
    return (
      <div className="rounded border border-surface-border bg-surface-muted/20 p-2 text-[11px] text-ink-muted">
        <CalendarClock className="mr-1 inline h-3 w-3" />
        No upcoming visits scheduled for this subject.
      </div>
    );
  }

  return (
    <div className="rounded border border-brand-200 bg-brand-50/40 p-2 text-[11px] text-ink-primary">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-3 w-3 text-brand-600" />
        <span className="font-semibold">Next visit</span>
        {nv.visit_name && (
          <span className="text-ink-muted">— {nv.visit_name}</span>
        )}
        {nv.is_reminder_active && (
          <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            <BellRing className="h-2.5 w-2.5" /> Reminder active
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] text-ink-secondary">
        Planned {visitWhen(nv) ? fmtDate(visitWhen(nv)!) : '—'}
        {nv.window_start_date && nv.window_end_date && (
          <>
            {' '}
            <span className="text-ink-muted">
              (window {fmtDate(nv.window_start_date)} → {fmtDate(nv.window_end_date)})
            </span>
          </>
        )}
        {q.data.upcoming_count > 1 && (
          <span className="ml-2 text-ink-muted">
            · {q.data.upcoming_count - 1} more upcoming
          </span>
        )}
      </div>
    </div>
  );
}
